import { z } from 'zod';

import { userAccountStateSchema } from '@/features/identity';

import type { MatchesSchema } from '@/shared/api/contract';

/**
 * Kontrakty odpowiedzi API modułu `Graph`, sprawdzane w czasie działania.
 *
 * ⚠ Wyliczenia jadą po drucie jako NAZWY (decyzja 71 — `JsonStringEnumConverter`
 * z camelCase). To nie jest kosmetyka JSON-a: `z.enum` odrzuca nieznaną wartość
 * NA GRANICY, podczas gdy nieznana liczba przeszłaby przez schemat i wpadła
 * w domyślną gałąź mapowania. Przestawienie wartości w enumie po stronie .NET
 * przestaje być cichą zmianą znaczenia całego frontu.
 */

export const relationKindSchema = z.enum(['dependsOn', 'has']);
export const ownerKindSchema = z.enum(['user', 'team']);

export type RelationKind = z.infer<typeof relationKindSchema>;
export type OwnerKind = z.infer<typeof ownerKindSchema>;

/** Cztery słowa w interfejsie, dwa typy w bazie — kierunek niesie flaga `reversed`. */
export type RelationDirection = 'has' | 'part-of' | 'depends-on' | 'used-by';

export function relationDirection(relation: RelationKind, reversed: boolean): RelationDirection {
  if (relation === 'has') {
    return reversed ? 'part-of' : 'has';
  }

  return reversed ? 'used-by' : 'depends-on';
}

export const ownerViewSchema = z.object({
  ownerKind: ownerKindSchema,
  ownerId: z.number(),
  displayName: z.string(),
  // Proweniencja (spec 9.1): „Team-Płatności" bez „odziedziczone z Płatności"
  // jest dla człowieka nieweryfikowalne.
  isLocal: z.boolean(),
  sourceElementId: z.number(),
  sourceElementTitle: z.string().nullable(),
  /**
   * Czy ten właściciel **schodzi w dół po `has`** (spec 9.1).
   *
   * ⛔ Sensowne wyłącznie przy właścicielu **lokalnym** — przy odziedziczonym mówi o cudzym
   * przypisaniu, którego stąd się nie zmienia.
   */
  inherit: z.boolean(),
  /**
   * Stan konta właściciela — nazwa idzie wyszarzona z dopiskiem, gdy konto nie jest żywe.
   *
   * ⭐ Właściciel **zostaje widoczny**, a nie znika: to jest odpowiedź na pytanie „kto za to
   * odpowiadał", a takie pytanie zadaje się właśnie wtedy, gdy człowieka już nie ma.
   *
   * ⚠ **Stan, nie flaga „wyłączone"**: konto założone importem czeka na wejście i nikt go nie
   * wyłączał. Zaraz po pierwszym imporcie tak wygląda prawie każdy właściciel.
   *
   * ⚠ `null` dla **zespołów** — zespół nie ma konta. ⛔ Nie mylić z `'active'`.
   */
  ownerState: userAccountStateSchema.nullable(),
});

export const graphNeighbourSchema = z.object({
  elementId: z.number(),
  title: z.string(),
  type: z.string().nullable(),
  relation: relationKindSchema,
  reversed: z.boolean(),
  relationId: z.number(),
  relationDescription: z.string().nullable(),
});

export const elementDetailSchema = z.object({
  id: z.number(),
  title: z.string(),
  type: z.string().nullable(),
  description: z.string().nullable(),
  owners: z.array(ownerViewSchema),
  relations: z.array(graphNeighbourSchema),
  activeTaskCount: z.number(),
  createdByUserId: z.number().nullable(),
  createdAtUtc: z.string(),
  updatedAtUtc: z.string(),
  // Uprawnienia liczy SERWER (spec 9.2: własność bywa dziedziczona, więc front
  // nie ma z czego ich wyprowadzić). Sterują widocznością akcji, nie dostępem.
  canEdit: z.boolean(),
  canDelete: z.boolean(),
  /**
   * Czy wolno tu ruszyć WŁASNOŚĆ (decyzja 110: właściciel, architekt albo admin).
   *
   * ⭐ Osobne od `canEdit`, bo to dwie różne odpowiedzialności: własność jest porządkiem
   * organizacyjnym, treść — wiedzą o systemie. Architekt prowadzi pierwszą, nie drugą.
   * ⚠ Liczy to SERWER; front nie ma z czego wyprowadzić dziedziczonej własności.
   */
  canManageOwners: z.boolean(),
});

export const elementSummarySchema = z.object({
  id: z.number(),
  title: z.string(),
  type: z.string().nullable(),
});

/**
 * Korzeń na ekranie wejściowym konstelacji: skrót elementu + „czy to ja go utworzyłem".
 * Ekran dzieli jedną listę na „punkty wejścia" (cudze) i „moje bez rodzica" (2026-08-23).
 */
export const rootElementSchema = elementSummarySchema.extend({
  createdByMe: z.boolean(),
});

/** Drzewko startowe jednym wywołaniem (decyzja 70). */
export const constellationNodeSchema = z.object({
  elementId: z.number(),
  title: z.string(),
  type: z.string().nullable(),
  parentId: z.number().nullable(),
  depth: z.number(),
  // Odróżnia liść od gałęzi uciętej sufitem głębokości — od tego zależy,
  // czy wiersz dostaje trójkąt rozwijania.
  childCount: z.number(),
  // ⚠ Liczby PRAWDZIWE, nie przycięte sufitem sekcji. Front porównuje je
  // z liczbą otrzymanych wierszy i sam wypisuje „⋯ jeszcze N".
  dependsOnCount: z.number(),
  usedByCount: z.number(),
  truncatedByCycle: z.boolean(),
});

export const constellationEdgeSchema = z.object({
  anchorElementId: z.number(),
  elementId: z.number(),
  title: z.string(),
  type: z.string().nullable(),
  reversed: z.boolean(),
  relationId: z.number(),
});

/**
 * Trójka liczb pracy przy wierszu drzewka (decyzja 35: zadania NA TYM elemencie,
 * WSZYSTKICH userów, BEZ agregacji poddrzewa).
 *
 * ⚠ „Śpi" i „draugr" to PRZECIWIEŃSTWA, nie odcienie tego samego: jedno czeka
 * na coś, co nadejdzie, drugie na coś, co już nie nadejdzie (decyzja 21).
 * Liczenie ich razem ukrywałoby martwe Surty w worku śpiących.
 */
export const elementWorkCountsSchema = z.object({
  elementId: z.number(),
  activeCount: z.number(),
  sleepingCount: z.number(),
  draugrCount: z.number(),
  highlightedCount: z.number(),
});

export type ElementWorkCounts = z.infer<typeof elementWorkCountsSchema>;

export const constellationViewSchema = z.object({
  rootElementId: z.number(),
  depth: z.number(),
  annotationDepth: z.number(),
  spine: z.array(constellationNodeSchema),
  annotations: z.array(constellationEdgeSchema),
  // Musi być pokazane, nie ukryte: niepełne drzewo wzięte za pełne prowadzi
  // do wniosków z brakujących danych.
  spineTruncated: z.boolean(),
  /**
   * Liczby pracy dla KAŻDEGO elementu tej odpowiedzi — szkieletu i celów adnotacji
   * (decyzja 173). ⛔ Pusta lista znaczy „nie policzono" (port nie odpowiedział
   * w budżecie), a nie „zero" — wiersz ma wtedy pokazać `?`, nie `—` (decyzja 167).
   */
  work: z.array(elementWorkCountsSchema),
});

/**
 * Agregat `Σ` — ta sama trójka liczb, ale zsumowana po **poddrzewie po `has`**
 * (decyzja 75, domyka pozycję otwartą B0).
 *
 * ⚠ Zawsze po `has`, także w soczewce zależności: „ile roboty jest w tej gałęzi"
 * ma znaczyć to samo niezależnie od tego, przez którą soczewkę się patrzy.
 */
export const elementWorkRollupSchema = z.object({
  elementId: z.number(),
  activeCount: z.number(),
  sleepingCount: z.number(),
  draugrCount: z.number(),
  highlightedCount: z.number(),
  /** `1` = liść; wtedy `Σ` byłby powtórzeniem liczb obok, więc go nie rysujemy. */
  subtreeSize: z.number(),
  /** Suma niepełna — przechód uderzył w sufit wierszy. */
  partial: z.boolean(),
});

export type ElementWorkRollup = z.infer<typeof elementWorkRollupSchema>;

/** Właściciele jednego elementu w odpowiedzi wsadowej — pod wiersze drzewka. */
export const elementOwnersSchema = z.object({
  elementId: z.number(),
  owners: z.array(ownerViewSchema),
});

export type ElementOwners = z.infer<typeof elementOwnersSchema>;

export type OwnerView = z.infer<typeof ownerViewSchema>;
export type GraphNeighbour = z.infer<typeof graphNeighbourSchema>;
export type ElementDetail = z.infer<typeof elementDetailSchema>;
export type ElementSummary = z.infer<typeof elementSummarySchema>;
export type RootElement = z.infer<typeof rootElementSchema>;
export type ConstellationNode = z.infer<typeof constellationNodeSchema>;
export type ConstellationEdge = z.infer<typeof constellationEdgeSchema>;
export type ConstellationView = z.infer<typeof constellationViewSchema>;

/**
 * Pozycja słownika typów elementu (decyzja 83).
 *
 * ⭐ Typ przestał być ozdobą w chwili, w której **selektor kampanii zaczął po nim wybierać**
 * — i to dokładną równością. Literówka „Serwis" / „serwis" po cichu wyklucza element ze zbioru,
 * a licznik postępu pokaże „34/120" i o brakujących nie powie ani słowa.
 */
export const elementTypeSchema = z.object({
  id: z.number(),
  name: z.string(),
  /** Czy typ jest **oferowany przy nowych elementach**. Wycofanie nie rusza elementów, które go mają. */
  isActive: z.boolean(),
  /** Ile żywych elementów go ma — pokazywane zawsze, nie dopiero przy odmowie usunięcia. */
  elementCount: z.number(),
});

export type ElementType = z.infer<typeof elementTypeSchema>;

export interface CreateElementInput {
  title: string;
  type?: string | null;
  description?: string | null;
}

export interface CreateRelationInput {
  fromElementId: number;
  toElementId: number;
  /** Tylko dwa typy jadą po drucie — `part-of` i `used-by` to te same krawędzie odwrócone. */
  type: 'has' | 'dependsOn';
  description?: string | null;
}

/** Wskazanie bytu organizacyjnego — usera albo zespołu. */
export interface OwnerRef {
  ownerKind: OwnerKind;
  ownerId: number;
}

/**
 * Przypisanie właścicieli — **wsadem, wszyscy albo nikt** (decyzja 97).
 *
 * `inherit` domyślnie **prawda**: własność spływa w dół po `has`, bo tak działa spec 9.1
 * i tak zachowuje się serwer, gdy pola nie ma. Wyłącza to dopiero `@inherit:nie` w komendzie —
 * i dlatego jest wspólne dla wsadu, a nie osobne przy każdej nazwie.
 */
export interface SetOwnersInput {
  elementId: number;
  owners: readonly OwnerRef[];
  inherit: boolean;
}

export interface UpdateElementInput {
  title?: string;
  type?: string | null;
  description?: string | null;
}

/**
 * Relacje elementu pogrupowane tak, jak rysuje je ekran (decyzja 51: stała
 * kolejność `has` → `depends-on` → `used-by`, wszystkie na jednym wcięciu).
 *
 * Grupowanie mieszka w modelu, a nie w komponencie, bo to reguła produktu,
 * a nie szczegół widoku — ten sam podział obowiązuje drzewko i ekran elementu.
 */
export function groupRelations(relations: readonly GraphNeighbour[]) {
  const groups: Record<RelationDirection, GraphNeighbour[]> = {
    has: [],
    'part-of': [],
    'depends-on': [],
    'used-by': [],
  };

  for (const relation of relations) {
    groups[relationDirection(relation.relation, relation.reversed)].push(relation);
  }

  return groups;
}

// ── Historia elementu (decyzja 104, krok K1b) ───────────────────────────────

export const elementEventKindSchema = z.enum([
  'created',
  'fieldChanged',
  'deleted',
  'relationAdded',
  'relationRemoved',
  'ownerAdded',
  'ownerRemoved',
]);

export type ElementEventKind = z.infer<typeof elementEventKindSchema>;

/**
 * Jedno **zdarzenie** historii — pojedyncze ogniwo albo **zwinięty przebieg importu**.
 *
 * ⭐ Zwijaniem zajmuje się SERWER. Element tknięty trzema przebiegami ma siedemnaście ogniw
 * importowych na dwadzieścia trzy — gdyby zwijał front, i tak musiałby ściągnąć wszystkie,
 * żeby pokazać sześć pozycji.
 *
 * ⚠ Przy `linkCount > 1` pola wartości są **puste celowo**: „zmienił 4 rzeczy" jest zdaniem
 * prawdziwym, a wybranie jednej z czterech na reprezentanta byłoby mylące.
 */
export const elementHistoryEventSchema = z.object({
  id: z.number(),
  importRunId: z.number().nullable(),
  linkCount: z.number(),
  kind: elementEventKindSchema.nullable(),
  field: z.string().nullable(),
  oldValue: z.string().nullable(),
  newValue: z.string().nullable(),
  relatedElementId: z.number().nullable(),
  relatedElementTitle: z.string().nullable(),
  actorUserId: z.number().nullable(),
  actorDisplayName: z.string().nullable(),
  /**
   * Stan konta autora zmiany — nazwisko zostaje, ale wyszarzone, gdy konto nie jest żywe.
   *
   * ⭐ Historia to zapis tego, co się stało, a nie lista, z której się wybiera — dlatego
   * nazwisko człowieka, który odszedł, MUSI tu zostać widoczne.
   *
   * ⚠ `null`, gdy zdarzenie **nie ma autora** (import, mechanizm).
   */
  actorState: userAccountStateSchema.nullable(),
  atUtc: z.string(),
});

export const elementHistoryPageSchema = z.object({
  items: z.array(elementHistoryEventSchema),
  /** `null` znaczy „to już koniec" — nie „nie wiem". */
  nextBefore: z.number().nullable(),
});

export type ElementHistoryEvent = z.infer<typeof elementHistoryEventSchema>;
export type ElementHistoryPage = z.infer<typeof elementHistoryPageSchema>;

// ── Spinacz z kontraktem API ────────────────────────────────────────────────
//
// Każdy schemat `zod` jest tu zestawiony z odpowiadającym mu schematem z dokumentu
// OpenAPI. Rozjazd — dodane, usunięte, przemianowane albo przetypowane pole —
// przestaje być niespodzianką w przeglądarce i staje się błędem `pnpm typecheck`.
//
// Dokument odświeża `applications/api/scripts/regenerate-openapi.ps1`, typy `pnpm gen:api`.
// Że dokument nie zwietrzał, pilnuje `OpenApiContractTests` po stronie API.

/**
 * Każda pozycja to jedna para „nasz typ ↔ schemat z dokumentu". `satisfies` zgłasza
 * błąd, gdy `Matches` nie wychodzi na `true` — a treść błędu mówi, w którą stronę
 * jest rozjazd.
 *
 * Obiekt jest eksportowany celowo: martwy kod wypadłby przy pierwszym sprzątaniu,
 * a wtedy ochrona zniknęłaby po cichu.
 */
export const elementsContract = {
  OwnerView: true satisfies MatchesSchema<OwnerView, 'OwnerView'>,
  GraphNeighbour: true satisfies MatchesSchema<GraphNeighbour, 'GraphNeighbour'>,
  ElementDetail: true satisfies MatchesSchema<ElementDetail, 'ElementDetail'>,
  ElementSummary: true satisfies MatchesSchema<ElementSummary, 'ElementSummary'>,
  ElementType: true satisfies MatchesSchema<ElementType, 'ElementTypeSummary'>,
  ElementWorkCounts: true satisfies MatchesSchema<ElementWorkCounts, 'ElementWorkCounts'>,
  ElementWorkRollup: true satisfies MatchesSchema<ElementWorkRollup, 'ElementWorkRollup'>,
  ElementOwners: true satisfies MatchesSchema<ElementOwners, 'ElementOwners'>,
  ConstellationNode: true satisfies MatchesSchema<ConstellationNode, 'ConstellationNode'>,
  ConstellationEdge: true satisfies MatchesSchema<ConstellationEdge, 'ConstellationEdge'>,
  ConstellationView: true satisfies MatchesSchema<ConstellationView, 'ConstellationView'>,
  ElementHistoryEvent: true satisfies MatchesSchema<ElementHistoryEvent, 'ElementHistoryEvent'>,
  ElementHistoryPage: true satisfies MatchesSchema<ElementHistoryPage, 'ElementHistoryPage'>,
} as const;
